import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Camera,
  CheckCircle2,
  Eye,
  PlayCircle,
  ShieldAlert,
  Sparkles,
} from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Combobox } from '@/components/ui/Combobox';
import { DataTable } from '@/components/ui/DataTable';
import type { Column } from '@/components/ui/DataTable';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Input } from '@/components/ui/Input';
import { Stepper, type StepItem } from '@/components/ui/Stepper';
import { SkeletonRows } from '@/components/ui/Skeleton';
import { BatchConfirmStep, type BatchConfirmRow } from '@/components/runs/BatchConfirmStep';
import { BatchResultCard } from '@/components/runs/BatchResultCard';
import { BatchScopeStep } from '@/components/runs/BatchScopeStep';
import {
  SystemAccessBadge,
  SystemAccessSummary,
  zugangsCodeLabel,
  zugangsStatus,
} from '@/components/runs/SystemAccessStatus';
import {
  MailTargetNotice,
  mailAckSentence,
  useMailTarget,
} from '@/components/runs/MailTargetNotice';
import {
  anySelectedRoutine,
  clearAll,
  initialSelection,
  selectedIds,
  selectedItems,
  selectionCounts,
  type BatchSelection,
} from '@/components/runs/batchSelection';
import {
  BELEG_BEISPIEL,
  VERZICHT_BEISPIEL,
  eineZeile,
} from '@/components/runs/snapshotWaiver';
import { useCustomers } from '@/hooks/useCustomers';
import { useRoutine, useRoutines, type Routine } from '@/hooks/useRoutines';
import {
  useCustomerMaintenancePreview,
  useStartBatchRuns,
  useStartRun,
  type BatchRunsResponse,
} from '@/hooks/useRuns';
import { useServiceTypes, type ServiceTypeOption } from '@/hooks/useServiceTypes';
import { useSystems } from '@/hooks/useSystems';
import type { System } from '@/types/api';

/**
 * /runs/new - Wartungs-Wizard (Sprint 5, WT-503/WT-505).
 *
 * Zwei Betriebsarten hinter derselben Route:
 *   a) Einzellauf (Default): eine Routine, beliebige Systeme -> ein Run.
 *   b) Komplettmodus (?customers=<id>&alle=1): alle passenden Routinen eines
 *      Kunden auf einmal. Ein Run trägt genau ein Playbook, also entsteht ein
 *      Lauf PRO ROUTINE - das UI führt das als EINEN Vorgang, benennt die
 *      Mehrzahl aber überall (Zusammenfassung + Ergebnisliste).
 *
 * Bewusst dieselbe Route statt einer eigenen Datei: Mode-/Snapshot-Schritt,
 * Idempotency-Key und Stepper sind identisch, und ?routine= bzw. ?systems=
 * bleiben von außen unverändert ansteuerbar.
 *
 * 4-Step-Stepper (Einzellauf):
 *   1. Routine wählen (skipped wenn ?routine=<code>)
 *   2. Systeme wählen (Customer-Filter, Multi-Select; ausgegraut wenn
 *      Scope excluded/eol/offline/external, Service-Typ nicht zur Routine
 *      passt oder aktives SSH-Profil fehlt — manual_only ist wählbar mit
 *      Badge "manuell" (S8-06 W2); ?customers=-Vorauswahl wählt nur
 *      Wählbare an)
 *   3. Mode + Snapshot (Dry-Run-Toggle, Snapshot-Toggle, Idempotency-Key)
 *   4. Confirm + Start (Summary-Card, destruktiver Button bei apply-Mode)
 *
 * Search-Params (TanStack-Router validateSearch):
 *   ?routine=<code>         - vorausgewählte Routine
 *   ?customers=<uuid,uuid>  - vorausgewählter Customer-Filter (kommagetrennt)
 *   ?systems=<uuid,uuid>    - konkrete System-Vorauswahl (z.B. von der
 *                             System-Detailseite); gewinnt gegen ?customers=
 *   ?alle=1                 - Komplettmodus für GENAU einen Kunden aus
 *                             ?customers=; ohne Kunde wirkungslos
 */

interface SearchSchema {
  routine?: string;
  customers?: string;
  /** Konkrete System-Vorauswahl (kommagetrennt), z.B. vom "Wartung starten"-
   *  Button der System-Detailseite - gewinnt gegen die Kunden-weite Vorauswahl. */
  systems?: string;
  /** Komplettmodus: alle passenden Routinen des Kunden in einem Vorgang. */
  alle?: boolean;
}

export const Route = createFileRoute('/runs/new')({
  validateSearch: (search: Record<string, unknown>): SearchSchema => {
    const out: SearchSchema = {};
    if (typeof search.routine === 'string') out.routine = search.routine;
    if (typeof search.customers === 'string') out.customers = search.customers;
    if (typeof search.systems === 'string') out.systems = search.systems;
    // Handgetippte Links tragen ?alle=1, Router-Links serialisieren `true`.
    if (search.alle === true || search.alle === '1' || search.alle === 'true') {
      out.alle = true;
    }
    return out;
  },
  component: WizardPage,
});

/**
 * Weiche zwischen Einzellauf und Komplettmodus. Der Komplettmodus braucht
 * genau einen Kunden (ein Run trägt Systeme genau eines Kunden); ohne den
 * fällt die Route auf den normalen Wizard zurück.
 */
function WizardPage(): JSX.Element {
  const search = Route.useSearch();
  const customerIds = search.customers ? search.customers.split(',').filter(Boolean) : [];
  const batchCustomerId = search.alle && customerIds.length === 1 ? customerIds[0] : null;
  // key: Kundenwechsel muss den Vorgang neu aufsetzen. Ohne ihn behielte der
  // Komplettmodus Auswahl, Idempotency-Key und Ergebnis des VORIGEN Kunden -
  // die Zähler zeigten dann fremde Systeme unter neuem Kundennamen.
  if (batchCustomerId) {
    return <CustomerBatchWizard key={batchCustomerId} customerId={batchCustomerId} />;
  }
  return <SingleRoutineWizard />;
}

type StepId = 'routine' | 'systems' | 'mode' | 'confirm';

interface SelectedSystem {
  id: string;
  hostname: string;
  customerName: string | null;
}

function SingleRoutineWizard(): JSX.Element {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const presetSlug = search.routine ?? null;
  const presetCustomers = useMemo(
    () => (search.customers ? search.customers.split(',').filter(Boolean) : []),
    [search.customers]
  );
  const presetSystemIds = useMemo(
    () => (search.systems ? search.systems.split(',').filter(Boolean) : []),
    [search.systems]
  );

  const [step, setStep] = useState<StepId>(presetSlug ? 'systems' : 'routine');
  const [completed, setCompleted] = useState<StepId[]>([]);
  const [routineCode, setRoutineCode] = useState<string | null>(presetSlug);
  const [systemIds, setSystemIds] = useState<string[]>([]);
  const [customerFilter, setCustomerFilter] = useState<string | null>(
    presetCustomers[0] ?? null
  );
  const [mode, setMode] = useState<'check' | 'apply'>('check');
  const [snapshot, setSnapshot] = useState(true);
  // Freiwillige Notizen dokumentieren den Verzicht am Lauf.
  const [waiverReason, setWaiverReason] = useState('');
  const [waiverEvidence, setWaiverEvidence] = useState('');
  // Gewohnheitsbremse aus der Antwort (3. Verzicht in 90 Tagen): solange sie
  // ungelesen ist, wird NICHT automatisch weggeklickt - sonst liest sie wieder
  // niemand.
  const [waiverWarnung, setWaiverWarnung] = useState<{
    text: string;
    runId: string;
  } | null>(null);
  const [idempotencyKey] = useState(() => generateIdempotencyKey());

  const routinesQuery = useRoutines();
  const routineDetail = useRoutine(routineCode ?? undefined);
  const startRun = useStartRun();

  // Kompatibilitäts-Filter: applicable_service_types der Routine (UUIDs oder
  // Codes) gegen den Service-Types-Katalog zu Codes auflösen. null = noch
  // nicht entscheidbar (keine Routine, oder Katalog fehlt) - dann ist NICHTS
  // wählbar, siehe ineligibleReason.
  const serviceTypesQuery = useServiceTypes();
  const applicableCodes = useMemo(
    () => resolveApplicableCodes(routineDetail.data ?? null, serviceTypesQuery.data),
    [routineDetail.data, serviceTypesQuery.data]
  );
  const applicableOsFamilies = useMemo(
    () => resolveApplicableOsFamilies(routineDetail.data ?? null),
    [routineDetail.data]
  );

  // Für den Confirm-Step: ausgewählte System-IDs -> {Hostname, Kunde} auflösen.
  // Selektion überlebt Kunden-Filter-Wechsel, daher laden wir hier ungefiltert
  // (auflösbar bleibt jede gewählte ID). Query dedupt sich über den Cache.
  const systemsQuery = useSystems({ limit: 200 });
  const customersQuery = useCustomers({ limit: 200 });

  // Systeme für die ?customers=-Vorauswahl. Bei genau einem Kunden
  // serverseitig gefiltert (Bestand > Seitenlimit), sonst best effort über
  // die ungefilterte Liste (Cache-dedupt mit systemsQuery).
  const firstPresetCustomer = presetCustomers[0];
  const presetSystemsQuery = useSystems(
    presetCustomers.length === 1 && firstPresetCustomer
      ? { customerId: firstPresetCustomer, limit: 200 }
      : { limit: 200 }
  );

  // ?customers=-/?systems=-Vorauswahl: einmalig nur kompatible Systeme MIT
  // aktivem SSH-Profil anwählen — exakt die Menge, die der Step auch anbietet.
  // Konkrete System-IDs (?systems=) gewinnen gegen die Kunden-weite Auswahl.
  const presetApplied = useRef(presetCustomers.length === 0 && presetSystemIds.length === 0);
  useEffect(() => {
    if (presetApplied.current) return;
    const routine = routineDetail.data;
    if (!routine) return;
    // UUID-basierte applicable_service_types brauchen den Katalog zum Auflösen.
    if (routine.applicable_service_types.length > 0 && !serviceTypesQuery.data) return;
    const systems = presetSystemsQuery.data?.data;
    if (!systems) return;
    presetApplied.current = true;
    const eligible = systems.filter(
      (s) => ineligibleReason(s, applicableCodes, applicableOsFamilies) === null
    );
    let ids: string[];
    if (presetSystemIds.length > 0) {
      const wanted = new Set(presetSystemIds);
      ids = eligible.filter((s) => wanted.has(s.id)).map((s) => s.id);
    } else {
      const wanted = new Set(presetCustomers);
      ids = eligible.filter((s) => wanted.has(s.customer_id)).map((s) => s.id);
    }
    if (ids.length > 0) setSystemIds(ids);
  }, [
    routineDetail.data,
    serviceTypesQuery.data,
    presetSystemsQuery.data,
    presetCustomers,
    presetSystemIds,
    applicableCodes,
    applicableOsFamilies,
  ]);

  // Routine-Wechsel (Zurück-Navigation) macht die Auswahl ungültig — leeren.
  const prevRoutineCode = useRef(routineCode);
  useEffect(() => {
    if (prevRoutineCode.current === routineCode) return;
    prevRoutineCode.current = routineCode;
    setSystemIds([]);
  }, [routineCode]);
  const selectedSystems: SelectedSystem[] = useMemo(() => {
    const custName = new Map<string, string>();
    for (const c of customersQuery.data?.data ?? []) custName.set(c.id, c.name);
    const byId = new Map<string, System>();
    for (const s of systemsQuery.data?.data ?? []) byId.set(s.id, s);
    return systemIds.map((sid) => {
      const s = byId.get(sid);
      return {
        id: sid,
        hostname: s?.hostname ?? sid.slice(0, 8),
        customerName: s ? custName.get(s.customer_id) ?? null : null,
      };
    });
  }, [systemIds, systemsQuery.data, customersQuery.data]);

  const steps: StepItem<StepId>[] = useMemo(() => {
    const list: StepItem<StepId>[] = [];
    if (!presetSlug) {
      list.push({ id: 'routine', label: 'Routine', description: 'Welche Wartung?' });
    }
    list.push(
      { id: 'systems', label: 'Systeme', description: 'Welche Hosts?' },
      { id: 'mode', label: 'Mode', description: 'Dry-Run / Snapshot' },
      { id: 'confirm', label: 'Confirm', description: 'Starten' }
    );
    return list;
  }, [presetSlug]);

  const goNext = (): void => {
    setCompleted((c) => (c.includes(step) ? c : [...c, step]));
    const idx = steps.findIndex((s) => s.id === step);
    const next = steps[idx + 1];
    if (next) setStep(next.id);
  };

  const goPrev = (): void => {
    const idx = steps.findIndex((s) => s.id === step);
    const prev = steps[idx - 1];
    if (prev) setStep(prev.id);
  };

  const destructive = Boolean(routineDetail.data?.is_destructive);
  const requiresSnapshot = Boolean(routineDetail.data?.requires_snapshot);
  const effSnapshot = mode === 'check' ? false : snapshot;
  // Nur im Echtlauf ist bei diesen Routinen ein expliziter Snapshot-Verzicht nötig.
  const waiverNoetig = mode === 'apply' && !snapshot && (requiresSnapshot || destructive);

  const onStart = (): void => {
    if (!routineDetail.data) return;
    if (systemIds.length === 0) return;
    startRun.mutate(
      {
        routineSlug: routineDetail.data.code,
        systemIds,
        checkMode: mode === 'check',
        snapshot: effSnapshot,
        ...(waiverNoetig
          ? {
              // Ohne Begründung fällt der Server auf snapshot_override zurück.
              ...(eineZeile(waiverReason) !== ''
                ? { snapshotWaiverReason: eineZeile(waiverReason) }
                : { snapshotOverride: true }),
              ...(eineZeile(waiverEvidence) !== ''
                ? { snapshotWaiverEvidence: eineZeile(waiverEvidence) }
                : {}),
            }
          : {}),
        idempotencyKey,
      },
      {
        onSuccess: (res) => {
          // Die Gewohnheitsbremse MUSS gelesen werden können, bevor die
          // Seite wechselt - der Lauf ist trotzdem gestartet.
          if (res.snapshot_waiver_warning) {
            setWaiverWarnung({ text: res.snapshot_waiver_warning, runId: res.run_id });
            return;
          }
          void navigate({ to: '/runs/$id', params: { id: res.run_id } });
        },
      }
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <header>
        <h1 className="text-2xl font-semibold">Wartung starten</h1>
        <p className="text-muted">Wizard für Dry-Run, Snapshot und Echtlauf.</p>
      </header>

      <Stepper
        steps={steps}
        active={step}
        completed={completed}
        onChange={(id) => setStep(id)}
        ariaLabel="Wartungs-Wizard-Schritte"
      />

      {step === 'routine' && (
        <RoutineStep
          loading={routinesQuery.isLoading}
          error={routinesQuery.error}
          routines={routinesQuery.data?.data ?? []}
          selectedCode={routineCode}
          onSelect={(code) => setRoutineCode(code)}
          onNext={goNext}
        />
      )}

      {step === 'systems' && (
        <SystemsStep
          routine={routineDetail.data ?? null}
          loadingRoutine={routineDetail.isLoading}
          applicableCodes={applicableCodes}
          applicableOsFamilies={applicableOsFamilies}
          customerId={customerFilter}
          onCustomerChange={setCustomerFilter}
          selectedSystemIds={systemIds}
          onSelectionChange={setSystemIds}
          onPrev={presetSlug ? null : goPrev}
          onNext={goNext}
        />
      )}

      {step === 'mode' && (
        <ModeStep
          mode={mode}
          onModeChange={setMode}
          snapshot={snapshot}
          onSnapshotChange={setSnapshot}
          requiresSnapshot={requiresSnapshot}
          destructive={destructive}
          waiverReason={waiverReason}
          onWaiverReasonChange={setWaiverReason}
          waiverEvidence={waiverEvidence}
          onWaiverEvidenceChange={setWaiverEvidence}
          idempotencyKey={idempotencyKey}
          onPrev={goPrev}
          onNext={goNext}
        />
      )}

      {step === 'confirm' && (
        <>
          <ConfirmStep
            routine={routineDetail.data ?? null}
            selectedSystems={selectedSystems}
            mode={mode}
            snapshotEnabled={mode === 'apply' && snapshot}
            waiverReason={waiverNoetig ? eineZeile(waiverReason) : null}
            waiverEvidence={waiverNoetig ? eineZeile(waiverEvidence) : null}
            idempotencyKey={idempotencyKey}
            onPrev={goPrev}
            onStart={onStart}
            starting={startRun.isPending}
            startError={startRun.error}
          />
          {waiverWarnung && (
            <SnapshotWaiverWarnung
              text={waiverWarnung.text}
              onWeiter={() =>
                void navigate({ to: '/runs/$id', params: { id: waiverWarnung.runId } })
              }
            />
          )}
        </>
      )}
    </div>
  );
}

/**
 * Gewohnheitsbremse des Servers (3. Snapshot-Verzicht desselben Kunden in 90
 * Tagen). Der Lauf ist bereits gestartet - dieser Kasten hält nur die
 * Weiterleitung an, bis der Text gelesen wurde. Stumm weiterleiten hieße:
 * die Warnung liest wieder niemand, und genau dagegen wurde sie gebaut.
 */
function SnapshotWaiverWarnung({
  text,
  onWeiter,
}: {
  text: string;
  onWeiter: () => void;
}): JSX.Element {
  return (
    <section
      role="alert"
      data-testid="waiver-warning"
      className="flex flex-wrap items-start gap-3 rounded-md border border-warning/40 bg-warning/10 p-3"
    >
      <AlertTriangle className="mt-0.5 size-5 shrink-0 text-warning" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-warning">
          Der Lauf ist gestartet &mdash; aber der Snapshot-Verzicht häuft sich.
        </p>
        <p className="mt-1 text-sm">{text}</p>
      </div>
      <Button size="sm" onClick={onWeiter} data-testid="waiver-warning-weiter">
        Gelesen, zum Lauf
      </Button>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Komplettmodus: "Kunde komplett warten"
// ---------------------------------------------------------------------------

type BatchStepId = 'scope' | 'mode' | 'confirm' | 'result';

const BATCH_STEPS: StepItem<BatchStepId>[] = [
  { id: 'scope', label: 'Umfang', description: 'Was passt worauf?' },
  { id: 'mode', label: 'Mode', description: 'Dry-Run / Snapshot' },
  { id: 'confirm', label: 'Bestätigen', description: 'Starten' },
  { id: 'result', label: 'Ergebnis', description: 'Erzeugte Läufe' },
];

/**
 * Ein Vorgang für den Nutzer, N Läufe für die Engine.
 *
 * Mode + Snapshot gelten für den ganzen Batch (der Endpunkt kennt nur ein
 * Flag-Paar): destruktiv/Snapshot-Pflicht wird deshalb über ALLE
 * ausgewählten Routinen aggregiert - die strengste gewinnt.
 */
function CustomerBatchWizard({ customerId }: { customerId: string }): JSX.Element {
  const previewQuery = useCustomerMaintenancePreview(customerId);
  const startBatch = useStartBatchRuns();

  const [step, setStep] = useState<BatchStepId>('scope');
  const [completed, setCompleted] = useState<BatchStepId[]>([]);
  const [selection, setSelection] = useState<BatchSelection>({});
  const [mode, setMode] = useState<'check' | 'apply'>('check');
  const [snapshot, setSnapshot] = useState(true);
  // Verzicht gilt für den GANZEN Batch (ein Flag-Paar am Endpunkt): der
  // Grund liegt in der Kundenumgebung, nicht in der einzelnen Routine.
  const [waiverReason, setWaiverReason] = useState('');
  const [waiverEvidence, setWaiverEvidence] = useState('');
  const [idempotencyKey, setIdempotencyKey] = useState(generateIdempotencyKey);
  const [result, setResult] = useState<BatchRunsResponse | null>(null);

  const preview = previewQuery.data ?? null;
  const groups = useMemo(() => preview?.groups ?? [], [preview]);
  const customerName = preview?.customer.name ?? 'diesen Kunden';

  // Erstauswahl = alles an. Genau einmal: ein Refetch darf eine bewusste
  // Abwahl nicht wieder anhaken.
  const seeded = useRef(false);
  useEffect(() => {
    if (seeded.current || !preview) return;
    seeded.current = true;
    setSelection(initialSelection(preview.groups));
  }, [preview]);

  const counts = useMemo(() => selectionCounts(groups, selection), [groups, selection]);
  const items = useMemo(() => selectedItems(groups, selection), [groups, selection]);
  const destructive = anySelectedRoutine(groups, selection, (r) => r.is_destructive);
  const requiresSnapshot = anySelectedRoutine(groups, selection, (r) => r.requires_snapshot);
  const effectiveSnapshot = mode === 'check' ? false : snapshot;
  // Ein Verzicht gilt für alle Routinen des Batches, sobald eine ihn benötigt.
  const waiverNoetig =
    mode === 'apply' && !snapshot && (requiresSnapshot || destructive);

  const routineNames = useMemo(() => {
    const map: Record<string, string> = {};
    for (const group of groups) map[group.routine.code] = group.routine.display_name;
    return map;
  }, [groups]);

  const confirmRows: BatchConfirmRow[] = useMemo(
    () =>
      items.map((item) => {
        const group = groups.find((g) => g.routine.code === item.routineSlug);
        return {
          routineSlug: item.routineSlug,
          displayName: group?.routine.display_name ?? item.routineSlug,
          systemCount: item.systemIds.length,
          destructive: Boolean(group?.routine.is_destructive),
        };
      }),
    [items, groups]
  );

  const markDone = (id: BatchStepId): void =>
    setCompleted((c) => (c.includes(id) ? c : [...c, id]));

  const goNext = (): void => {
    markDone(step);
    const idx = BATCH_STEPS.findIndex((s) => s.id === step);
    const next = BATCH_STEPS[idx + 1];
    if (next) setStep(next.id);
  };

  const goPrev = (): void => {
    const idx = BATCH_STEPS.findIndex((s) => s.id === step);
    const prev = BATCH_STEPS[idx - 1];
    if (prev) setStep(prev.id);
  };

  const onStart = (): void => {
    if (items.length === 0) return;
    startBatch.mutate(
      {
        customerId,
        items,
        checkMode: mode === 'check',
        snapshot: effectiveSnapshot,
        // Der Verzicht gilt für alle Einträge des Batches.
        ...(waiverNoetig
          ? {
              // Ohne Begründung fällt der Server auf snapshot_override zurück.
              ...(eineZeile(waiverReason) !== ''
                ? { snapshotWaiverReason: eineZeile(waiverReason) }
                : { snapshotOverride: true }),
              ...(eineZeile(waiverEvidence) !== ''
                ? { snapshotWaiverEvidence: eineZeile(waiverEvidence) }
                : {}),
            }
          : {}),
        idempotencyKey,
      },
      {
        onSuccess: (res) => {
          setResult(res);
          markDone('confirm');
          // Auch das Ergebnis als "erledigt" markieren, damit man nach einem
          // Blick zurück auf den Umfang wieder zur Lauf-Liste springen kann.
          markDone('result');
          setStep('result');
        },
      }
    );
  };

  /**
   * Der Batch-Endpunkt erkennt einen Wiederholungsklick allein am
   * Idempotency-Key - den Request-Inhalt vergleicht er NICHT. Wer nach dem
   * Ergebnis zurückgeht und Umfang, Mode oder Snapshot ändert, bekäme mit
   * demselben Key die ALTEN Läufe zurück (created:false) und hätte in
   * Wahrheit nichts gestartet. Also: sobald ein Ergebnis vorliegt und der
   * Auftrag sich ändert, neuer Key und altes Ergebnis weg.
   */
  const invalidateResult = (): void => {
    if (!result) return;
    setResult(null);
    setIdempotencyKey(generateIdempotencyKey());
    startBatch.reset();
    setCompleted((c) => c.filter((id) => id !== 'result'));
  };

  // Teilfehler nachziehen: nur die gescheiterten Routinen bleiben angewählt -
  // und zwar mit GENAU den Systemen, die der Nutzer gewählt hatte. Ein Reset
  // auf "alle Systeme der Routine" würde bewusst Abgewähltes zurückholen.
  // Neuer Key, sonst antwortet der Batch-Endpunkt mit dem alten Ergebnis.
  const retryFailed = (slugs: string[]): void => {
    const kept: BatchSelection = {};
    for (const slug of slugs) kept[slug] = selectedIds(selection, slug);
    setSelection({ ...clearAll(groups), ...kept });
    setIdempotencyKey(generateIdempotencyKey());
    setResult(null);
    startBatch.reset();
    setCompleted([]);
    setStep('scope');
  };

  return (
    <div className="flex flex-col gap-4">
      <header>
        <h1 className="text-2xl font-semibold">Kunde komplett warten</h1>
        {/* Nicht "jedem System": es bleiben regelmäßig Systeme außen vor
            (Scope, SSH-Profil, kein passender Typ) - die stehen mit Grund im
            Umfang-Schritt. */}
        <p className="text-muted">
          {customerName}: das Tool ordnet die passenden Routinen den Systemen zu und legt
          pro Routine einen Lauf an.
        </p>
      </header>

      <Stepper
        steps={BATCH_STEPS}
        active={step}
        completed={completed}
        onChange={(id) => setStep(id)}
        ariaLabel="Schritte der Komplettwartung"
      />

      {step === 'scope' && (
        <BatchScopeStep
          customerId={customerId}
          customerName={customerName}
          groups={groups}
          excluded={preview?.excluded ?? []}
          selection={selection}
          onSelectionChange={(next) => {
            invalidateResult();
            setSelection(next);
          }}
          loading={previewQuery.isLoading}
          error={previewQuery.error}
          onNext={goNext}
        />
      )}

      {step === 'mode' && (
        <ModeStep
          mode={mode}
          onModeChange={(m) => {
            invalidateResult();
            setMode(m);
          }}
          snapshot={snapshot}
          onSnapshotChange={(v) => {
            invalidateResult();
            setSnapshot(v);
          }}
          requiresSnapshot={requiresSnapshot}
          destructive={destructive}
          waiverReason={waiverReason}
          onWaiverReasonChange={(v) => {
            invalidateResult();
            setWaiverReason(v);
          }}
          waiverEvidence={waiverEvidence}
          onWaiverEvidenceChange={(v) => {
            invalidateResult();
            setWaiverEvidence(v);
          }}
          idempotencyKey={idempotencyKey}
          onPrev={goPrev}
          onNext={goNext}
        />
      )}

      {step === 'confirm' && (
        <BatchConfirmStep
          customerName={customerName}
          rows={confirmRows}
          counts={counts}
          mode={mode}
          snapshotEnabled={effectiveSnapshot}
          snapshotWaiverReason={waiverNoetig ? eineZeile(waiverReason) : null}
          snapshotWaiverEvidence={waiverNoetig ? eineZeile(waiverEvidence) : null}
          idempotencyKey={idempotencyKey}
          onPrev={goPrev}
          onStart={onStart}
          starting={startBatch.isPending}
          startError={startBatch.error}
        />
      )}

      {step === 'result' && result && (
        <BatchResultCard
          customerId={customerId}
          customerName={customerName}
          result={result}
          routineNames={routineNames}
          onRetryFailed={retryFailed}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Weiter-Button (oben + unten)
// ---------------------------------------------------------------------------

/**
 * "Weiter"-Button für Steps mit langen Listen (Routinen, Systeme): sitzt
 * zusätzlich im Card-Header, damit man nicht ans Listenende scrollen muss.
 *
 * Bewusst KEINE Sticky-Leiste: <main> im Layout hat overflow-x-hidden und ist
 * damit Scroll-Container — position:sticky im Content pinnt dort nicht am
 * Viewport. Der Header-Button braucht außerdem keine z-index-Abstimmung mit
 * Tabellen-Head/Combobox-Dropdown und kostet auf kleinen Schirmen keine Höhe.
 *
 * Eine Komponente für beide Positionen, damit Label/Zaehler und
 * Disabled-Logik nie auseinanderlaufen; `position` unterscheidet die beiden
 * per aria-label, damit Screenreader nicht zweimal dasselbe Ziel ohne
 * Unterscheidung ansagen.
 */
interface StepNextButtonProps {
  onNext: () => void;
  disabled: boolean;
  label: string;
  /** aria-label OHNE Positionszusatz; "(oben)"/"(unten)" hängt die Komponente an. */
  ariaLabel: string;
  position: 'oben' | 'unten';
  testId: string;
}

function StepNextButton({
  onNext,
  disabled,
  label,
  ariaLabel,
  position,
  testId,
}: StepNextButtonProps): JSX.Element {
  return (
    <Button
      rightIcon={<ArrowRight className="size-4" />}
      disabled={disabled}
      onClick={onNext}
      aria-label={`${ariaLabel} (${position})`}
      data-testid={testId}
    >
      {label}
    </Button>
  );
}

// ---------------------------------------------------------------------------
// Step 1: Routine
// ---------------------------------------------------------------------------

interface RoutineStepProps {
  loading: boolean;
  error: Error | null;
  routines: Routine[];
  selectedCode: string | null;
  onSelect: (code: string) => void;
  onNext: () => void;
}

function RoutineStep({
  loading,
  error,
  routines,
  selectedCode,
  onSelect,
  onNext,
}: RoutineStepProps): JSX.Element {
  const columns = useMemo((): ReadonlyArray<Column<Routine>> => {
    return [
      {
        id: 'auswahl',
        header: ' ',
        headerClassName: 'w-8',
        filter: false,
        cell: (r) => (
          <input
            type="radio"
            name="routine"
            checked={selectedCode === r.code}
            onChange={() => onSelect(r.code)}
            aria-label={`Routine ${r.display_name} wählen`}
            className="size-4 accent-primary"
          />
        ),
      },
      {
        id: 'name',
        header: 'Name',
        filterValue: (r) => `${r.display_name} ${r.code}`,
        cell: (r) => (
          <div>
            <div className="font-medium">{r.display_name}</div>
            <div className="font-mono text-xs text-muted">{r.code}</div>
          </div>
        ),
      },
      {
        id: 'tier',
        header: 'Tier',
        filter: 'select',
        filterValue: (r) => r.tier,
        cell: (r) => (
          <Badge
            tone={
              r.tier === 'production'
                ? 'success'
                : r.tier === 'experimental'
                  ? 'warning'
                  : 'neutral'
            }
          >
            {r.tier}
          </Badge>
        ),
      },
      {
        id: 'snapshot-pflicht',
        header: 'Snapshot Pflicht?',
        filter: 'select',
        filterValue: (r) => (r.requires_snapshot ? 'ja' : 'nein'),
        cell: (r) =>
          r.requires_snapshot ? (
            <Badge tone="primary">ja</Badge>
          ) : (
            <span className="text-xs text-muted">nein</span>
          ),
      },
    ];
  }, [selectedCode, onSelect]);

  const nextButton = (position: 'oben' | 'unten'): JSX.Element => (
    <StepNextButton
      onNext={onNext}
      disabled={!selectedCode}
      label="Weiter"
      ariaLabel="Weiter"
      position={position}
      testId={`routine-next-${position === 'oben' ? 'top' : 'bottom'}`}
    />
  );
  return (
    <Card
      title="Routine wählen"
      description="Welche Wartung soll laufen?"
      actions={nextButton('oben')}
    >
      {loading && <SkeletonRows rows={4} cols={3} />}
      {error && <ErrorAlert error={error} />}
      {!loading && !error && (
        <DataTable
          caption="Verfügbare Routinen"
          columns={columns}
          rows={routines}
          rowKey={(r) => r.id}
          storageKey="run-neu-routinen"
          onRowClick={(r) => onSelect(r.code)}
          rowSelected={(r) => selectedCode === r.code}
        />
      )}
      <footer className="mt-3 flex justify-end">{nextButton('unten')}</footer>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Step 2: Systeme
// ---------------------------------------------------------------------------

interface SystemsStepProps {
  routine: Routine | null;
  loadingRoutine: boolean;
  /**
   * Aufgelöste Service-Type-Codes der Routine. `null` = noch nicht
   * entscheidbar (Katalog lädt oder fällt aus) - dann ist kein System
   * wählbar. LEER = die Routine bindet nicht über Typen; leer ist seit dem
   * 10.09.2026 KEINE Wildcard mehr.
   */
  applicableCodes: ReadonlySet<string> | null;
  /** os_family-Werte der Routine; ODER-verknuepft mit applicableCodes. */
  applicableOsFamilies: ReadonlySet<string>;
  customerId: string | null;
  onCustomerChange: (id: string | null) => void;
  selectedSystemIds: string[];
  onSelectionChange: (ids: string[]) => void;
  onPrev: (() => void) | null;
  onNext: () => void;
}

function SystemsStep({
  routine,
  loadingRoutine,
  applicableCodes,
  applicableOsFamilies,
  customerId,
  onCustomerChange,
  selectedSystemIds,
  onSelectionChange,
  onPrev,
  onNext,
}: SystemsStepProps): JSX.Element {
  const customersQuery = useCustomers({ limit: 200 });
  const filter: { customerId?: string; limit: number } = { limit: 200 };
  if (customerId) filter.customerId = customerId;
  const systemsQuery = useSystems(filter);

  const allSystems: System[] = useMemo(
    () => systemsQuery.data?.data ?? [],
    [systemsQuery.data]
  );

  // Hostname-Labels sind NICHT eindeutig (funf Kunden mit Host "voip") —
  // die Tabelle zeigt darum den eindeutigen Systemnamen plus den Kunden.
  const customerNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of customersQuery.data?.data ?? []) m.set(c.id, c.name);
    return m;
  }, [customersQuery.data]);

  // Wählbarkeit pro System: in_scope oder manual_only + Service-Typ ODER
  // os_family kompatibel zur Routine + aktives SSH-Profil vorhanden (Details
  // siehe ineligibleReason unten).
  const reasonBySystem = useMemo(() => {
    const map = new Map<string, IneligibleReason | null>();
    for (const s of allSystems) {
      map.set(s.id, ineligibleReason(s, applicableCodes, applicableOsFamilies));
    }
    return map;
  }, [allSystems, applicableCodes, applicableOsFamilies]);

  const eligibleIds = useMemo(
    () => allSystems.filter((s) => reasonBySystem.get(s.id) === null).map((s) => s.id),
    [allSystems, reasonBySystem]
  );

  const allEligibleSelected =
    eligibleIds.length > 0 && eligibleIds.every((id) => selectedSystemIds.includes(id));

  const toggle = useCallback((id: string): void => {
    onSelectionChange(
      selectedSystemIds.includes(id)
        ? selectedSystemIds.filter((x) => x !== id)
        : [...selectedSystemIds, id]
    );
  }, [onSelectionChange, selectedSystemIds]);

  const selectAllEligible = (): void => {
    if (allEligibleSelected) {
      onSelectionChange([]);
    } else {
      onSelectionChange(eligibleIds);
    }
  };

  const selectedCount = selectedSystemIds.length;
  const nextButton = (position: 'oben' | 'unten'): JSX.Element => (
    <StepNextButton
      onNext={onNext}
      disabled={selectedCount === 0}
      label={`Weiter (${selectedCount})`}
      ariaLabel={`Weiter, ${selectedCount} ${selectedCount === 1 ? 'System' : 'Systeme'} ausgewählt`}
      position={position}
      testId={`systems-next-${position === 'oben' ? 'top' : 'bottom'}`}
    />
  );

  const columns = useMemo((): ReadonlyArray<Column<System>> => {
    return [
      {
        id: 'auswahl',
        header: ' ',
        headerClassName: 'w-8',
        filter: false,
        cell: (sys) => {
          const eligible = (reasonBySystem.get(sys.id) ?? null) === null;
          const checked = selectedSystemIds.includes(sys.id);
          return (
            <input
              type="checkbox"
              checked={checked}
              onChange={() => toggle(sys.id)}
              onClick={(e) => e.stopPropagation()}
              disabled={!eligible}
              aria-label={`${sys.hostname} auswaehlen`}
              className="size-4 accent-primary"
            />
          );
        },
      },
      {
        id: 'system',
        header: 'System',
        filterValue: (sys) => {
          const name = sys.display_name ?? sys.hostname;
          return sys.display_name && sys.display_name !== sys.hostname
            ? `${name} ${sys.hostname}`
            : name;
        },
        cell: (sys) => (
          <>
            <span className="font-mono text-xs">
              {sys.display_name ?? sys.hostname}
            </span>
            {sys.display_name && sys.display_name !== sys.hostname && (
              <span className="ml-1 text-xs text-muted">({sys.hostname})</span>
            )}
          </>
        ),
      },
      {
        id: 'kunde',
        header: 'Kunde',
        className: 'max-w-[14rem]',
        filterValue: (sys) => customerNameById.get(sys.customer_id) ?? '',
        cell: (sys) => (
          <span className="block truncate text-xs">
            {customerNameById.get(sys.customer_id) ?? '–'}
          </span>
        ),
      },
      {
        id: 'rolle',
        header: 'Rolle',
        filter: 'select',
        filterValue: (sys) => sys.role ?? '',
        cell: (sys) => <Badge tone="neutral">{sys.role ?? '–'}</Badge>,
      },
      {
        // OS steht hier, seit eine Routine allein über
        // applicable_os_families binden kann: sonst sieht der
        // Bedienende eine Hostliste ohne jeden Beleg, warum ein
        // Host darin steht. os_family ist außerdem freier Text -
        // "Debian 12" statt "debian" fällt still heraus, und das
        // sieht man nur, wenn der Wert dasteht.
        id: 'betriebssystem',
        header: 'Betriebssystem',
        filter: 'select',
        filterValue: (sys) => sys.os_family ?? 'nicht gepflegt',
        cell: (sys) =>
          sys.os_family ? (
            <Badge
              tone={osFamilyTrifft(sys, applicableOsFamilies) ? 'success' : 'neutral'}
              data-testid={`system-os-family-${sys.id}`}
            >
              <span className="text-[10px]">{sys.os_family}</span>
            </Badge>
          ) : (
            <span
              className="text-xs text-muted"
              title="systems.os_family ist nicht gepflegt - eine Routine, die über die Betriebssystem-Familie bindet, erreicht dieses System deshalb nicht."
              data-testid={`system-os-family-${sys.id}`}
            >
              nicht gepflegt
            </span>
          ),
      },
      {
        id: 'scope',
        header: 'Scope',
        filter: 'select',
        filterValue: (sys) => (sys.scope === 'in_scope' || !sys.scope ? 'in_scope' : sys.scope),
        cell: (sys) =>
          sys.scope === 'in_scope' || !sys.scope ? (
            <Badge tone="success">in_scope</Badge>
          ) : (
            <Badge tone="neutral">{sys.scope}</Badge>
          ),
      },
      {
        // Zugang: Hinweis, keine Sperre. Ein System, auf das der
        // Client zuletzt nicht kam, bleibt anhakbar - die
        // Prüfung kann veraltet sein, und Colin kennt seine
        // Umgebung besser als das Tool.
        id: 'zugang',
        header: 'Zugang',
        filter: 'select',
        filterValue: (sys) => {
          const status = zugangsStatus(sys);
          if (status === 'ok') return 'Zugang stand';
          if (status === 'fehlt') return `kein Zugang · ${zugangsCodeLabel(sys.access_code)}`;
          if (status === 'unklar') return 'gemeldet, nicht einzuordnen';
          return 'noch nie geprüft';
        },
        cell: (sys) => (
          <SystemAccessBadge system={sys} data-testid={`system-access-${sys.id}`} />
        ),
      },
      {
        id: 'hinweis',
        header: 'Hinweis',
        filterValue: (sys) => systemHinweisFilter(sys, reasonBySystem.get(sys.id) ?? null),
        cell: (sys) => {
          const reason = reasonBySystem.get(sys.id) ?? null;
          return (
            <span className="flex flex-wrap items-center gap-1">
              {sys.scope === 'manual_only' && (
                <Badge tone="warning" data-testid={`system-manual-only-${sys.id}`}>
                  manuell
                </Badge>
              )}
              {reason === 'incompatible' && (
                <Badge tone="neutral" data-testid={`system-incompatible-${sys.id}`}>
                  nicht kompatibel
                </Badge>
              )}
              {reason === 'incompatible_os' && (
                <span
                  title={`Routine gilt für ${[...applicableOsFamilies].sort().join(', ')} - dieses System ist ${sys.os_family ?? 'unbekannt'}`}
                >
                  <Badge
                    tone="neutral"
                    data-testid={`system-incompatible-os-${sys.id}`}
                  >
                    anderes Betriebssystem
                  </Badge>
                </span>
              )}
              {/* Eigener Grund statt "anderes Betriebssystem": Hier
                  fehlt ein Stammdatum, und die Abhilfe steht eine
                  Seite weiter. Genau diese Systeme sind der Anlass
                  der Familienbindung - sie als "anderes
                  Betriebssystem" zu beschriften, führt von der
                  Ursache weg. */}
              {reason === 'no_os_family' && (
                <Link
                  to="/systems/$id"
                  params={{ id: sys.id }}
                  onClick={(e) => e.stopPropagation()}
                  title={`Die Routine bindet über die Betriebssystem-Familie (${[...applicableOsFamilies].sort().join(', ')}), an diesem System ist keine gepflegt. Systemdaten öffnen.`}
                >
                  <Badge
                    tone="warning"
                    className="hover:underline"
                    data-testid={`system-no-os-family-${sys.id}`}
                  >
                    OS nicht gepflegt
                  </Badge>
                </Link>
              )}
              {reason === 'catalog_missing' && (
                <span title="Der Service-Typ-Katalog (GET /service-types) ist nicht geladen. Ohne ihn lässt sich die Bindung der Routine nicht prüfen - deshalb ist nichts wählbar. Seite neu laden.">
                  <Badge
                    tone="warning"
                    data-testid={`system-catalog-missing-${sys.id}`}
                  >
                    Katalog nicht geladen
                  </Badge>
                </span>
              )}
              {reason === 'no_ssh_profile' && (
                <Link
                  to="/systems/$id"
                  params={{ id: sys.id }}
                  search={{ tab: 'connections' }}
                  onClick={(e) => e.stopPropagation()}
                  title="Connection-Profile dieses Systems öffnen"
                >
                  <Badge
                    tone="warning"
                    className="hover:underline"
                    data-testid={`system-no-ssh-${sys.id}`}
                  >
                    kein SSH-Profil
                  </Badge>
                </Link>
              )}
              {(reason === null || reason === 'out_of_scope') &&
                sys.scope !== 'manual_only' && (
                  <span className="text-xs text-muted">–</span>
                )}
            </span>
          );
        },
      },
    ];
  }, [
    applicableOsFamilies,
    customerNameById,
    reasonBySystem,
    selectedSystemIds,
    toggle,
  ]);

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <span>Systeme wählen</span>
          {routine && <Badge tone="neutral">{routine.code}</Badge>}
        </span>
      }
      description={
        routine
          ? `Routine: ${routine.display_name}. Ausgegraute Systeme sind nicht wählbar.`
          : 'Routine erst wählen.'
      }
      actions={routine ? nextButton('oben') : undefined}
    >
      {loadingRoutine && <SkeletonRows rows={3} cols={3} />}
      {routine && (
        <>
          <div className="mb-3 flex flex-wrap items-end gap-3">
            <div className="min-w-[220px]">
              <Combobox
                label="Filter Kunde"
                value={customerId}
                onChange={(v) => onCustomerChange(v)}
                options={(customersQuery.data?.data ?? []).map((c) => ({
                  value: c.id,
                  label: c.name,
                  hint: c.slug,
                }))}
                placeholder="Alle Kunden"
              />
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={selectAllEligible}
              data-testid="systems-select-all"
            >
              {allEligibleSelected
                ? 'Auswahl leeren'
                : `Alle wählbaren (${eligibleIds.length})`}
            </Button>
            <span className="text-xs text-muted">
              {selectedSystemIds.length} ausgewählt
            </span>
          </div>

          <p className="mb-1 text-sm" aria-live="polite" data-testid="systems-selectable-count">
            <strong>{eligibleIds.length}</strong> von {allSystems.length} Systemen wählbar
          </p>
          {/* Zugangslage vor dem Start, nicht danach (Vorfall 13.08.2026). */}
          <SystemAccessSummary
            systems={allSystems}
            className="mb-1"
            data-testid="systems-access-summary"
          />
          <p className="mb-3 text-xs text-muted">
            Ausgegraut: Scope excluded/eol/offline/external, Service-Typ passt nicht
            zur Routine (&laquo;nicht kompatibel&raquo;) oder kein aktives
            SSH-Connection-Profil (&laquo;kein SSH-Profil&raquo; &mdash; Klick öffnet
            den Connections-Tab des Systems). Systeme mit Scope manual_only sind
            wählbar und mit &laquo;manuell&raquo; markiert.
          </p>
          <p className="mb-3 text-xs text-muted">
            Spalte &laquo;Zugang&raquo;: was der wt-client bei seiner letzten
            Vorabprüfung gemeldet hat &mdash; eine Beobachtung mit Zeitstempel, kein
            Live-Test. &laquo;Noch nie geprüft&raquo; ist kein Fehler, und ein System
            ohne Zugang bleibt wählbar: die Meldung kann veraltet sein. Was zu tun
            ist, steht im Tooltip der Marke.
          </p>

          {systemsQuery.isLoading && <SkeletonRows rows={5} cols={4} />}
          {systemsQuery.isError && <ErrorAlert error={systemsQuery.error} />}
          {allSystems.length === 0 && !systemsQuery.isLoading && (
            <p className="text-sm text-muted">
              Keine Systeme gefunden.
              {customerId && ' Filter prüfen.'}
            </p>
          )}
          {allSystems.length > 0 && (
            <DataTable
              caption="Anwendbare Systeme"
              columns={columns}
              rows={allSystems}
              rowKey={(sys) => sys.id}
              storageKey="run-neu-systeme"
              defaultPageSize={50}
              onRowClick={(sys) => {
                if ((reasonBySystem.get(sys.id) ?? null) === null) toggle(sys.id);
              }}
              rowSelected={(sys) => selectedSystemIds.includes(sys.id)}
              rowMuted={(sys) => (reasonBySystem.get(sys.id) ?? null) !== null}
              rowTestId={(sys) => `system-row-${sys.id}`}
            />
          )}
        </>
      )}
      <footer className="mt-3 flex justify-between">
        {onPrev ? (
          <Button variant="secondary" leftIcon={<ArrowLeft className="size-4" />} onClick={onPrev}>
            Zurück
          </Button>
        ) : (
          <span />
        )}
        {nextButton('unten')}
      </footer>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Step 3: Mode
// ---------------------------------------------------------------------------

interface ModeStepProps {
  mode: 'check' | 'apply';
  onModeChange: (m: 'check' | 'apply') => void;
  snapshot: boolean;
  onSnapshotChange: (v: boolean) => void;
  requiresSnapshot: boolean;
  destructive: boolean;
  /** Freiwillige Begründung zur Dokumentation des Snapshot-Verzichts. */
  waiverReason: string;
  onWaiverReasonChange: (v: string) => void;
  /** Freiwilliger Ersatzsicherungs-Beleg; bei destruktiven Routinen empfohlen. */
  waiverEvidence: string;
  onWaiverEvidenceChange: (v: string) => void;
  idempotencyKey: string;
  onPrev: () => void;
  onNext: () => void;
}

function ModeStep({
  mode,
  onModeChange,
  snapshot,
  onSnapshotChange,
  requiresSnapshot,
  destructive,
  waiverReason,
  onWaiverReasonChange,
  waiverEvidence,
  onWaiverEvidenceChange,
  idempotencyKey,
  onPrev,
  onNext,
}: ModeStepProps): JSX.Element {
  // Auch destruktive Routinen erlauben den Verzicht mit freiwilligen Notizen.
  const effectiveSnapshot = mode === 'check' ? false : snapshot;
  const noSnapshotWarning = mode === 'apply' && !effectiveSnapshot;
  const overridesRoutine = noSnapshotWarning && (requiresSnapshot || destructive);

  return (
    <Card title="Mode + Snapshot" description="Dry-Run zuerst, dann Echtlauf mit Snapshot.">
      <div className="flex flex-col gap-4">
        <fieldset>
          <legend className="mb-2 text-sm font-medium">Mode</legend>
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
            <label
              className={[
                'flex cursor-pointer items-start gap-3 rounded-md border p-3 text-sm',
                mode === 'check'
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:bg-surface-2',
              ].join(' ')}
            >
              <input
                type="radio"
                name="mode"
                value="check"
                aria-label="Dry-Run Mode"
                checked={mode === 'check'}
                onChange={() => onModeChange('check')}
                className="mt-0.5 size-4 accent-primary"
              />
              <span>
                <span className="flex items-center gap-1.5 font-medium">
                  <Eye className="size-4" aria-hidden="true" />
                  Dry-Run (--check)
                </span>
                <span className="mt-1 block text-xs text-muted">
                  Ansible läuft im Check-Mode. Diff wird angezeigt, keine Änderung am System.
                </span>
              </span>
            </label>
            <label
              className={[
                'flex cursor-pointer items-start gap-3 rounded-md border p-3 text-sm',
                mode === 'apply'
                  ? 'border-danger bg-danger/5'
                  : 'border-border hover:bg-surface-2',
              ].join(' ')}
            >
              <input
                type="radio"
                name="mode"
                value="apply"
                aria-label="Echtlauf Mode"
                checked={mode === 'apply'}
                onChange={() => onModeChange('apply')}
                className="mt-0.5 size-4 accent-primary"
              />
              <span>
                <span className="flex items-center gap-1.5 font-medium">
                  <PlayCircle className="size-4 text-danger" aria-hidden="true" />
                  Echtlauf (apply)
                </span>
                <span className="mt-1 block text-xs text-muted">
                  Reale Änderung am System. Mit Pre-Snapshot für Rollback.
                </span>
              </span>
            </label>
          </div>
        </fieldset>

        <fieldset>
          <legend className="mb-2 text-sm font-medium">Pre-Snapshot via Proxmox</legend>
          <label
            className={[
              'flex items-start gap-3 rounded-md border border-border p-3 text-sm',
              mode === 'check' ? 'opacity-60' : '',
            ].join(' ')}
          >
            <input
              type="checkbox"
              aria-label="Pre-Snapshot via Proxmox aktivieren"
              checked={effectiveSnapshot}
              disabled={mode === 'check'}
              aria-describedby={overridesRoutine ? 'no-snapshot-warning' : undefined}
              onChange={(e) => onSnapshotChange(e.target.checked)}
              className="mt-0.5 size-4 accent-primary"
              data-testid="snapshot-toggle"
            />
            <span>
              <span className="flex items-center gap-1.5 font-medium">
                <Camera className="size-4" aria-hidden="true" />
                PVE-Snapshot vor Run
                {destructive && mode === 'apply' ? (
                  <Badge tone="danger">
                    <span className="inline-flex items-center gap-1">
                      <ShieldAlert className="size-3" /> Pflicht (destruktiv) &ndash;
                      Beleg beim Verzicht empfohlen
                    </span>
                  </Badge>
                ) : (
                  requiresSnapshot &&
                  mode === 'apply' && (
                    <Badge tone="primary">
                      <span className="inline-flex items-center gap-1">
                        <ShieldAlert className="size-3" /> Von Routine gefordert
                      </span>
                    </Badge>
                  )
                )}
              </span>
              <span className="mt-1 block text-xs text-muted">
                {mode === 'check'
                  ? 'Im Dry-Run nicht relevant.'
                  : 'Tool nimmt vor Run via PVE-API einen Snapshot. 1-Klick-Rollback bei Problemen.'}
              </span>
            </span>
          </label>

          {noSnapshotWarning && (
            <div
              className={[
                'mt-2 flex items-start gap-2 rounded-md border p-3 text-sm',
                overridesRoutine
                  ? 'border-danger/40 bg-danger/10 text-danger'
                  : 'border-warning/40 bg-warning/10 text-warning',
              ].join(' ')}
              role="alert"
              id="no-snapshot-warning"
              data-testid="no-snapshot-warning"
            >
              <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
              <span>
                Ohne Snapshot kein Rollback möglich. Bei Fehlern hilft nur Backup-Restore.
                {overridesRoutine && (
                  <>
                    {' '}
                    Diese Routine sieht einen Snapshot vor. Eine freiwillige Begründung
                    des Verzichts steht danach am Lauf und im Wartungsbericht.
                  </>
                )}
              </span>
            </div>
          )}

          {/* Freiwillige Notizen machen den Verzicht später nachvollziehbar. */}
          {overridesRoutine && (
            <div
              className="mt-2 flex flex-col gap-3 rounded-md border border-border p-3"
              data-testid="waiver-form"
            >
              <label className="flex flex-col gap-1 text-sm">
                <span className="font-medium">
                  Begründung für den Snapshot-Verzicht{' '}
                  <span className="text-muted">(optional)</span>
                </span>
                <textarea
                  value={waiverReason}
                  onChange={(e) => onWaiverReasonChange(e.target.value)}
                  rows={2}
                  placeholder={VERZICHT_BEISPIEL}
                  className="rounded-md border border-border bg-surface px-3 py-2 text-sm text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  data-testid="waiver-reason"
                />
                <span className="text-xs text-muted">
                  Optional. Warum ist hier kein Snapshot möglich oder sinnvoll? Steht danach am Lauf und im Wartungsbericht.
                </span>
              </label>
              <label className="flex flex-col gap-1 text-sm">
                <span className="font-medium">
                  Ersatzsicherungs-Beleg{' '}
                  {destructive ? (
                    <span className="text-warning">(empfohlen bei destruktiver Routine)</span>
                  ) : (
                    <span className="text-muted">(optional)</span>
                  )}
                </span>
                <Input
                  value={waiverEvidence}
                  onChange={(e) => onWaiverEvidenceChange(e.target.value)}
                  placeholder={BELEG_BEISPIEL}
                  data-testid="waiver-evidence"
                />
                <span className="text-xs text-muted">
                  Optional. Was sichert diesen Stand stattdessen, z. B. PBS-Backup mit Zeitpunkt?
                </span>
              </label>
            </div>
          )}
        </fieldset>

        <p className="text-xs text-muted">
          Idempotency-Key: <code className="font-mono">{idempotencyKey}</code>
        </p>
      </div>

      <footer className="mt-3 flex justify-between">
        <Button variant="secondary" leftIcon={<ArrowLeft className="size-4" />} onClick={onPrev}>
          Zurück
        </Button>
        <Button
          rightIcon={<ArrowRight className="size-4" />}
          onClick={onNext}
          data-testid="mode-next"
        >
          Weiter
        </Button>
      </footer>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Step 4: Confirm
// ---------------------------------------------------------------------------

interface ConfirmStepProps {
  routine: Routine | null;
  selectedSystems: SelectedSystem[];
  mode: 'check' | 'apply';
  snapshotEnabled: boolean;
  /** Begründeter Snapshot-Verzicht; null = kein Verzicht fällig. */
  waiverReason?: string | null;
  waiverEvidence?: string | null;
  idempotencyKey: string;
  onPrev: () => void;
  onStart: () => void;
  starting: boolean;
  startError: Error | null;
}

function ConfirmStep({
  routine,
  selectedSystems,
  mode,
  snapshotEnabled,
  waiverReason,
  waiverEvidence,
  idempotencyKey,
  onPrev,
  onStart,
  starting,
  startError,
}: ConfirmStepProps): JSX.Element {
  const isDestructive = mode === 'apply';
  const systemCount = selectedSystems.length;
  const [confirmed, setConfirmed] = useState(!isDestructive);
  const mailTarget = useMailTarget();

  // Ein Run trägt Systeme genau eines Kunden - bei gemischter Auswahl bleibt
  // der Name im Mail-Hinweis offen ("des Kunden").
  const mailCustomerName = useMemo(() => {
    const names = new Set(
      selectedSystems
        .map((s) => s.customerName)
        .filter((n): n is string => n != null && n !== '')
    );
    return names.size === 1 ? ([...names][0] ?? null) : null;
  }, [selectedSystems]);
  const mailAck = mailAckSentence(mailTarget, mailCustomerName);

  // Reset confirm-flag wenn der User Mode toggelt.
  useEffect(() => {
    setConfirmed(!isDestructive);
  }, [isDestructive]);

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <span>Confirm + Start</span>
          {isDestructive && <Badge tone="danger">Echtlauf</Badge>}
        </span>
      }
      description="Letzter Check vor Run."
    >
      <div className="flex flex-col gap-3">
        <SummaryRow label="Routine" value={routine?.display_name ?? '–'} />
        <SummaryRow label="Code" value={routine?.code ?? '–'} mono />
        <div className="flex flex-col gap-1 border-b border-border pb-2 text-sm">
          <span className="text-xs uppercase tracking-wider text-muted">
            Systeme ({systemCount})
            {isDestructive && (
              <span className="ml-1 text-danger">– werden verändert</span>
            )}
          </span>
          {selectedSystems.length > 0 ? (
            <ul className="flex flex-col gap-1" data-testid="confirm-systems-list">
              {selectedSystems.map((s) => (
                <li key={s.id} className="flex flex-wrap items-center gap-2">
                  <code className="font-mono text-xs">{s.hostname}</code>
                  {s.customerName && (
                    <span className="text-xs text-muted">· {s.customerName}</span>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <span className="text-xs text-muted">Keine Systeme gewählt.</span>
          )}
        </div>
        <SummaryRow
          label="Mode"
          value={mode === 'check' ? 'Dry-Run (--check)' : 'Echtlauf (apply)'}
        />
        <SummaryRow label="Pre-Snapshot" value={snapshotEnabled ? 'ja' : 'nein'} />
        {/* Der Verzicht steht sichtbar in der letzten Zusammenfassung - was
            hier bestätigt wird, steht danach wörtlich am Lauf. */}
        {waiverReason != null && waiverReason !== '' && (
          <div
            className="flex flex-col gap-1 rounded-md border border-warning/40 bg-warning/10 p-3 text-sm"
            data-testid="confirm-waiver"
          >
            <span className="font-medium text-warning">
              Snapshot-Verzicht mit Begründung
            </span>
            <span>{waiverReason}</span>
            {waiverEvidence != null && waiverEvidence !== '' && (
              <span className="text-xs text-muted">
                Ersatzsicherung: {waiverEvidence}
              </span>
            )}
          </div>
        )}
        <SummaryRow label="Idempotency-Key" value={idempotencyKey} mono />

        {/* Direkt vor Checkbox + Start-Knopf: das Mailziel ist das Letzte,
            was vor dem Klick gelesen wird. */}
        <MailTargetNotice mode={mode} customerName={mailCustomerName} />

        {isDestructive && (
          <label className="flex items-start gap-2 rounded-md border border-danger/40 bg-danger/5 p-3 text-sm">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="mt-0.5 size-4 accent-danger"
              data-testid="confirm-destructive"
            />
            <span>
              Ich verstehe, dass dies <strong>{systemCount} reale Systeme</strong> verändert.
              {!snapshotEnabled && ' Ohne Snapshot ist kein Rollback möglich.'}
              {mailAck}
            </span>
          </label>
        )}

        {startError && <ErrorAlert error={startError} />}
      </div>

      <footer className="mt-4 flex justify-between">
        <Button variant="secondary" leftIcon={<ArrowLeft className="size-4" />} onClick={onPrev}>
          Zurück
        </Button>
        <Button
          variant={isDestructive ? 'danger' : 'primary'}
          leftIcon={isDestructive ? <ShieldAlert className="size-4" /> : <Sparkles className="size-4" />}
          onClick={onStart}
          loading={starting}
          disabled={!confirmed || systemCount === 0 || !routine}
          data-testid="wizard-start"
          size="lg"
        >
          {isDestructive ? 'Echtlauf starten' : 'Dry-Run starten'}
        </Button>
      </footer>

      <p className="mt-2 inline-flex items-center gap-1 text-xs text-muted">
        <CheckCircle2 className="size-3" /> Run-Detail öffnet sich automatisch nach Start.
      </p>
    </Card>
  );
}

function SummaryRow({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}): JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-border pb-2 text-sm last:border-b-0">
      <span className="min-w-[140px] text-xs uppercase tracking-wider text-muted">
        {label}
      </span>
      <span className={mono ? 'font-mono text-xs' : ''}>{value}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

type IneligibleReason =
  | 'out_of_scope'
  | 'incompatible'
  | 'incompatible_os'
  | 'no_os_family'
  | 'catalog_missing'
  | 'no_ssh_profile';

/** Sichtbare Hinweis-Texte als Filtergrundlage (nicht der Platzhalter –). */
function systemHinweisFilter(sys: System, reason: IneligibleReason | null): string {
  const parts: string[] = [];
  if (sys.scope === 'manual_only') parts.push('manuell');
  if (reason === 'incompatible') parts.push('nicht kompatibel');
  if (reason === 'incompatible_os') parts.push('anderes Betriebssystem');
  if (reason === 'no_os_family') parts.push('OS nicht gepflegt');
  if (reason === 'catalog_missing') parts.push('Katalog nicht geladen');
  if (reason === 'no_ssh_profile') parts.push('kein SSH-Profil');
  return parts.join(' ');
}

/** Leere Menge als stabile Referenz - spart neue Sets in jedem Render. */
const EMPTY_CODES: ReadonlySet<string> = new Set<string>();

/**
 * Löst routine.applicable_service_types zu Service-Type-CODES auf.
 *
 * Das Feld liefert historisch UUIDs (Katalog-IDs); einzelne Routinen (z.B.
 * AI-generierte) können bereits Codes enthalten. Jeder Eintrag wird daher
 * erst gegen den Katalog (id -> code) aufgelöst, sonst als Code übernommen.
 *
 * `null` heißt "noch nicht entscheidbar" (keine Routine, oder der Katalog
 * fehlt) und führt in ineligibleReason zu 'catalog_missing' - also zu KEINEM
 * wählbaren System. Früher hieß null "keine Einschraenkung", und ein
 * Ausfall von GET /service-types machte damit jede typgebundene Routine auf
 * der ganzen Flotte wählbar.
 *
 * Die LEERE Menge ist etwas anderes: "diese Routine bindet nicht über
 * Typen" - sie kann trotzdem über applicable_os_families binden.
 */
function resolveApplicableCodes(
  routine: Routine | null,
  serviceTypes: ServiceTypeOption[] | undefined
): ReadonlySet<string> | null {
  if (!routine) return null;
  // Leer heißt "nicht ueber Typen gebunden" - NICHT "keine Einschraenkung".
  // Die Routine kann trotzdem über applicable_os_families binden, und wenn
  // auch die leer ist, passt sie auf nichts (Server: run_engine/batch.py).
  if (routine.applicable_service_types.length === 0) return EMPTY_CODES;
  if (!serviceTypes) return null;
  const idToCode = new Map<string, string>();
  for (const st of serviceTypes) idToCode.set(st.id, st.code.toLowerCase());
  const codes = new Set<string>();
  for (const entry of routine.applicable_service_types) {
    codes.add(idToCode.get(entry) ?? entry.toLowerCase());
  }
  return codes;
}


/**
 * Löst routine.applicable_os_families zu kleingeschriebenen os_family-Werten auf.
 *
 * Anders als die Service-Typen braucht das keinen Katalog: os_family ist ein
 * freier String am System. Ein älterer Server liefert das Feld nicht - dann
 * ist die Menge leer, und es bleibt bei der Bindung über Typen.
 */
/**
 * Trägt dieses System die os_family, über die die Routine bindet?
 *
 * Nur für die Färbung des Badges: grün heißt "an DIESEM Wert hängt die
 * Eignung", grau heisst "der Wert steht hier bloß". Ohne die Unterscheidung
 * ist die Spalte Dekoration.
 */
function osFamilyTrifft(sys: System, applicableOsFamilies: ReadonlySet<string>): boolean {
  const wert = (sys.os_family ?? '').trim().toLowerCase();
  return wert !== '' && applicableOsFamilies.has(wert);
}

function resolveApplicableOsFamilies(routine: Routine | null): ReadonlySet<string> {
  const roh = routine?.applicable_os_families;
  if (!roh || roh.length === 0) return EMPTY_CODES;
  return new Set(roh.map((f) => f.trim().toLowerCase()).filter(Boolean));
}

/**
 * Warum ist ein System (nicht) wählbar? null = wählbar.
 *
 * `manual_only` ist WÄHLBAR (Spec S8-06 Nachtrag W2: die Engine lässt
 * manual_only bewusst zu, der Wizard darf nicht strenger sein) — es bekommt
 * im Step nur ein Hinweis-Badge "manuell". Blockiert bleiben
 * excluded/eol/offline/external.
 *
 * `service_type_codes` und `has_active_ssh_profile` liefert die
 * Systems-Liste seit Sprint 8. Der Fallback (Felder fehlen -> bewusst
 * NICHT filtern) bleibt für Robustheit bei älteren Responses.
 */
function ineligibleReason(
  sys: System,
  applicableCodes: ReadonlySet<string> | null,
  applicableOsFamilies: ReadonlySet<string> = EMPTY_CODES
): IneligibleReason | null {
  if (sys.scope && sys.scope !== 'in_scope' && sys.scope !== 'manual_only') {
    return 'out_of_scope';
  }
  // FAIL CLOSED: Solange der Service-Typ-Katalog nicht geladen ist, lässt
  // sich über die Bindung nichts sagen - und "nichts sagen" hieß hier bis
  // zum 10.09.2026 "alles erlauben". Fällt GET /service-types aus (500,
  // abgelaufenes Token, Netz), blieb jede typgebundene Routine also ohne
  // wirksame Bindung, der Bedienende konnte jedes System anhaken, und
  // POST /runs prüft serverseitig weder Typ noch os_family nach. Das ist
  // genau das Schadensbild, das dieses Paket schließt - nur über einen
  // Ladefehler statt über einen Tippfehler.
  if (applicableCodes === null) return 'catalog_missing';

  // Zwei Stufen, ODER-verknuepft: Service-Typ ODER os_family (Spiegel von
  // _ineligible_reason in run_engine/batch.py). Der zweite Weg existiert, weil
  // der erste an der Pflege des Katalogs hängt - ein Debian ohne Primärtyp
  // erreichte sonst keine Routine.
  //
  // Die os_family-Stufe steht BEWUSST außerhalb der service_type_codes-Frage:
  // Fehlt das Feld in einer Antwort (älterer Server), galt früher gar keine
  // Bindungsprüfung - die alte Wildcard lebte im Frontend als Sonderfall
  // weiter. Eine Routine, die nur über Familien bindet, braucht die
  // Typenliste ohnehin nicht.
  const codes = (sys.service_type_codes ?? []).map((c) => c.toLowerCase());
  const trifftTyp = codes.some((c) => applicableCodes.has(c));
  const osFamily = (sys.os_family ?? '').trim().toLowerCase();
  const trifftOs = osFamily !== '' && applicableOsFamilies.has(osFamily);
  if (!trifftTyp && !trifftOs) {
    if (applicableCodes.size === 0 && applicableOsFamilies.size > 0) {
      // Getrennte Gründe, weil die Abhilfe eine andere ist: bei
      // 'no_os_family' fehlt ein Stammdatum (Feld pflegen bzw.
      // Zabbix-Ingest), bei 'incompatible_os' ist das System wirklich etwas
      // anderes. Ein gemeinsames "anderes Betriebssystem" über einem Debian,
      // an dem nur niemand die os_family gepflegt hat, führt von der Ursache
      // weg - und das sind genau die Systeme, um die es hier geht.
      return osFamily === '' ? 'no_os_family' : 'incompatible_os';
    }
    return 'incompatible';
  }
  if (sys.has_active_ssh_profile === false) return 'no_ssh_profile';
  return null;
}

function generateIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `run-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}
